import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { 
  AppBar, 
  Toolbar, 
  Typography, 
  Button, 
  Box, 
  Avatar, 
  Menu, 
  MenuItem, 
  ListItemIcon,
  ListItemText,
  useTheme,
  Tooltip
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import BarChartIcon from '@mui/icons-material/BarChart';
import ScienceIcon from '@mui/icons-material/Science';
import { getUserFirstname, getUserLastname, hasRole } from '../../../Services/Global';
import { Roles } from '../../../Models/Enums';
import logger from '../../../Services/LoggingService';
import { createFlexStyles } from '../../../theme/layoutStyles';

// Import images
import Logo from '../../../Assets/SVG/logo.svg';

interface NavbarProps {
  sellerFeatureActive: boolean;
  modelerFeatureActive: boolean;
}

/**
 * Navbar component - Material UI version of Navbar
 *
 * @param {NavbarProps} props Component properties
 * @return {React.ReactElement} The Navbar component
 */
export const Navbar = ({ sellerFeatureActive, modelerFeatureActive }: NavbarProps) => {
  const isAuthenticated = useIsAuthenticated();
  const navigate = useNavigate();
  const { instance } = useMsal();
  const theme = useTheme();
  const flexStyles = createFlexStyles(theme);

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [lastName, setLastname] = useState('');
  const [firstName, setFirstname] = useState('');
  
  const showDropdown = Boolean(anchorEl);

  /**
   * Handles dropdown menu opening
   */
  const handleMenuClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  /**
   * Handles dropdown menu closing
   */
  const handleMenuClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  /**
   * Handles logout
   */
  const handleLogout = useCallback(() => {
    instance.logoutRedirect().catch((e: unknown) => {
      logger.error("Logout redirect failed", e as Error, null, {
        component: 'Navbar',
        method: 'handleLogout'
      });
    });
    handleMenuClose();
  }, [instance, handleMenuClose]);

  /**
   * Navigate to settings
   */
  const handleSettings = useCallback(() => {
    navigate('/admin');
    handleMenuClose();
  }, [navigate, handleMenuClose]);

  /**
   * Navigate to ProductDesignForm with dummy query parameters for testing
   */
  const handleProductDesignTest = useCallback(() => {
    // Create test query parameters that would work with ProductDesignForm
    const testParams = new URLSearchParams({
      process: 'Beer%20Cooling', // URL encoded "Beer Cooling"
      productline: 'Hygienic',
      country: 'SE' // Sweden as default test country
    });
    
    // Navigate to ProductDesignForm with test parameters
    navigate(`/productdesign?${testParams.toString()}`);
  }, [navigate]);

  useEffect(() => {
    setFirstname(getUserFirstname);
    setLastname(getUserLastname);
  }, []);

  return (
    <AppBar position="static" color="default" elevation={0} sx={{ 
      backgroundColor: 'white',
      borderBottom: '1px solid',
      borderColor: theme.palette.grey[300]
    }}>
      <Toolbar>
        {/* Logo */}
        <Box sx={{ ...flexStyles.flexCenter, mr: 2 }}>
          <Box component="img" src={Logo} alt="logo" sx={{ height: 40 }} className="emelie-navbar--logo" />
        </Box>

        {/* Title */}
        <Box sx={{ flexGrow: 1, ...flexStyles.flexCenter, ml: 2 }}>
          <Typography 
            variant="h5" 
            sx={{ 
              fontWeight: 500, 
              color: theme.palette.primary.dark, 
              fontSize: '1.5rem', 
              fontFamily: "Segoe UI Semibold" 
            }}
          >
            AI Design Wizard
          </Typography>
          {!process.env['REACT_APP_DEPLOYED_ENV']?.includes('prod') && (
            <Typography variant="body1" sx={{ ml: 1, color: theme.palette.primary.dark }}>
              ({process.env['REACT_APP_DEPLOYED_ENV']})
            </Typography>
          )}
        </Box>

        {/* Navigation Buttons */}
        {hasRole(...[Roles[Roles.BlindTestUser].toString()]) && (
          <Button 
            variant="outlined" 
            sx={{ mr: 1, borderRadius: 1 }}
            onClick={() => navigate('/blindtests')}
          >
            Blind tests
          </Button>
        )}

        {modelerFeatureActive && 
          (hasRole(Roles[Roles.Modeler].toString()) || hasRole(Roles[Roles.SME].toString())) && (
          <Button 
            variant="outlined" 
            sx={{ mr: 1, borderRadius: 1 }}
            onClick={() => navigate('/modeling/dashboard')}
          >
            Modeling
          </Button>
        )}

        {sellerFeatureActive && hasRole(...[Roles[Roles.Seller].toString()]) && (
          <Button 
            variant="outlined" 
            sx={{ mr: 1, borderRadius: 1 }}
            onClick={() => navigate('/dashboard')}
          >
            Seller dashboard
          </Button>
        )}

        {/* User Profile */}
        {isAuthenticated && (
          <Box sx={flexStyles.flexCenter}>
            <Box 
              sx={{ 
                ...flexStyles.flexCenter,
                cursor: 'pointer',
                padding: 1,
                borderRadius: 1,
                '&:hover': {
                  backgroundColor: theme.palette.grey[100]
                }
              }}
              onClick={handleMenuClick}
            >
              <Avatar 
                sx={{ 
                  width: 32, 
                  height: 32,
                  bgcolor: theme.palette.error.main,
                  color: 'white',
                  fontSize: '0.875rem',
                  fontWeight: 500 
                }}
              >
                {firstName.charAt(0)}{lastName.charAt(0)}
              </Avatar>
              <Typography variant="body2" sx={{ ml: 1, color: theme.palette.primary.dark }}>
                {`${firstName} ${lastName}`}
              </Typography>
            </Box>

            <Menu
              anchorEl={anchorEl}
              open={showDropdown}
              onClose={handleMenuClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              PaperProps={{
                elevation: 3,
                sx: {
                  width: 200,
                  borderRadius: 1,
                  mt: 0.5
                }
              }}
            >
              <MenuItem onClick={handleSettings}>
                <ListItemIcon>
                  <SettingsIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Settings</ListItemText>
              </MenuItem>
              <MenuItem onClick={handleLogout}>
                <ListItemIcon>
                  <LogoutIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Logout</ListItemText>
              </MenuItem>
            </Menu>
          </Box>
        )}
      </Toolbar>
    </AppBar>
  );
};
